import { Component, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { SidebarComponent } from './sidebar.component';
import { TopbarComponent } from '../topbar/topbar.component';
import { LayoutService } from '../../../services/layout/app.layout.service';
import { filter, Subscription } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { IdleTimeoutService } from '../../../services/idle-timeout.service';

@Component({
	selector: 'app-layout',
	templateUrl: './layout.component.html',
	styleUrl: './layout.component.scss'
})
export class LayoutComponent implements OnInit, OnDestroy {
	overlayMenuOpenSubscription: Subscription;
	spinnerSubscription: Subscription;

	menuOutsideClickListener: any;
	profileMenuOutsideClickListener: any;

	@ViewChild(SidebarComponent) appSidebar!: SidebarComponent;

	@ViewChild(TopbarComponent) appTopbar!: TopbarComponent;

	isSpinnerActive: boolean = false;

	constructor(
		public layoutService: LayoutService,
		private renderer: Renderer2,
		private router: Router,
		private idleTimeoutService: IdleTimeoutService
	) {
		this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
			if (!this.menuOutsideClickListener) {
				this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
					const isOutsideClicked = !(
						this.appSidebar.el.nativeElement.isSameNode(event.target) ||
						this.appSidebar.el.nativeElement.contains(event.target) ||
						this.appTopbar.menuButton.nativeElement.isSameNode(event.target) ||
						this.appTopbar.menuButton.nativeElement.contains(event.target)
					);

					if (isOutsideClicked) {
						this.hideMenu();
					}
				});
			}

			if (!this.profileMenuOutsideClickListener) {
				this.profileMenuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
					const isOutsideClicked = !(
						this.appTopbar.menu.nativeElement.isSameNode(event.target) ||
						this.appTopbar.menu.nativeElement.contains(event.target) ||
						this.appTopbar.topbarMenuButton.nativeElement.isSameNode(event.target) ||
						this.appTopbar.topbarMenuButton.nativeElement.contains(event.target)
					);

					if (isOutsideClicked) {
						this.hideProfileMenu();
					}
				});
			}

			if (this.layoutService.state.staticMenuMobileActive) {
				this.blockBodyScroll();
			}
		});

		this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
			this.hideMenu();
		});

		this.spinnerSubscription = this.layoutService.showSpinner$.subscribe((status) => {
			this.isSpinnerActive = status;
		});
	}

	ngOnInit(): void {
		// Start idle timeout detection and session keep-alive
		this.idleTimeoutService.start();
	}

	hideProfileMenu() {
		this.layoutService.state.profileSidebarVisible = false;
		if (this.profileMenuOutsideClickListener) {
			this.profileMenuOutsideClickListener();
			this.profileMenuOutsideClickListener = null;
		}
	}

	hideMenu() {
		this.layoutService.state.overlayMenuActive = false;
		this.layoutService.state.staticMenuMobileActive = false;
		this.layoutService.state.menuHoverActive = false;
		if (this.menuOutsideClickListener) {
			this.menuOutsideClickListener();
			this.menuOutsideClickListener = null;
		}
		this.unblockBodyScroll();
	}

	blockBodyScroll() {
		if (document.body.classList) {
			document.body.classList.add('blocked-scroll');
		} else {
			document.body.className += ' blocked-scroll';
		}
	}

	unblockBodyScroll() {
		if (document.body.classList) {
			document.body.classList.remove('blocked-scroll');
		} else {
			document.body.className = document.body.className.replace(
				new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'),
				' '
			);
		}
	}

	get containerClass() {
		return {
			'layout-theme-light': this.layoutService.config().colorScheme === 'dark',
			'layout-theme-dark': this.layoutService.config().colorScheme === 'dark',
			'layout-overlay': this.layoutService.config().menuMode === 'overlay',
			'layout-static': this.layoutService.config().menuMode === 'static',
			'layout-static-inactive':
				this.layoutService.state.staticMenuDesktopInactive &&
				this.layoutService.config().menuMode === 'static',
			'layout-overlay-active': this.layoutService.state.overlayMenuActive,
			'layout-mobile-active': this.layoutService.state.staticMenuMobileActive,
			'p-input-filled': this.layoutService.config().inputStyle === 'filled',
			'p-ripple-disabled': !this.layoutService.config().ripple
		};
	}

	ngOnDestroy() {
		// Stop idle timeout detection and session keep-alive
		this.idleTimeoutService.stop();

		if (this.overlayMenuOpenSubscription) {
			this.overlayMenuOpenSubscription.unsubscribe();
		}

		if (this.spinnerSubscription) {
			this.spinnerSubscription.unsubscribe();
		}

		if (this.menuOutsideClickListener) {
			this.menuOutsideClickListener();
		}
	}
}
